import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabaseClient";

// ============================================================
// Data layer for the Membership & Events app.
// Same proven pattern as the store app: hooks return [rows, dispatch, load]
// with ADD / UPDATE / DELETE actions, each reading/writing Supabase.
// Components use camelCase; DB uses snake_case; we translate here.
// ============================================================

const camelToSnakeMaps = {
  mbr_members: { dietReviewed: "diet_reviewed", firstName: "first_name", lastName: "last_name", noContact: "no_contact", addrStreet: "addr_street", addrCity: "addr_city", addrState: "addr_state", addrZip: "addr_zip", tierId: "tier_id", joinedOn: "joined_on", renewsOn: "renews_on" },
  mbr_dashboard: { widgetKey: "widget_key" },
  mbr_events: { mealOptions: "meal_options", eventDate: "event_date", allowGuests: "allow_guests", guestPrice: "guest_price", inviteMessage: "invite_message", mealDeadline: "meal_deadline", guestTypes: "guest_types", planApproved: "plan_approved", planApprovedAt: "plan_approved_at", managerId: "manager_id", cancelledAt: "cancelled_at", cancelReason: "cancel_reason", cancelPending: "cancel_pending", cancelChecklist: "cancel_checklist", surveyOpen: "survey_open" },
  // invitations is the heart of the app: links a member to an event and tracks
  // invited -> responded -> meal -> invoiced -> paid -> attended.
  mbr_invitations: {
    memberId: "member_id", eventId: "event_id", mealChoice: "meal_choice",
    guestCount: "guest_count", guestDetails: "guest_details",
    invitedAt: "invited_at", invoicedAt: "invoiced_at", paidAt: "paid_at",
  },
  mbr_event_expenses: { eventId: "event_id" },
  mbr_prospects: { sourceEventId: "source_event_id", invitedByMemberId: "invited_by_member_id", guestType: "guest_type", convertedMemberId: "converted_member_id" },
  mbr_messages: { recipientCount: "recipient_count", audienceLabel: "audience_label", eventId: "event_id" },
  mbr_checklist_defaults: { leadDays: "lead_days" },

  mbr_team: { lastActive: "last_active" },
  mbr_provider_reviews: { providerId: "provider_id", eventId: "event_id", reviewedAt: "reviewed_at" },
  mbr_survey_responses: { eventId: "event_id", memberId: "member_id", submittedAt: "submitted_at" },
  mbr_announcements: { bodyHtml: "body_html", pdfUrl: "pdf_url" },
  mbr_payments: { paidAt: "paid_at", eventId: "event_id", memberId: "member_id", sourceType: "source_type", sourceId: "source_id", refundedAt: "refunded_at" },
  mbr_vendors: { eventId: "event_id", spaceName: "space_name", depositPaid: "deposit_paid", depositAmount: "deposit_amount" },
};

function toRow(table, obj) {
  const map = camelToSnakeMaps[table] || {};
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (k === "id") continue;
    out[map[k] || k] = v;
  }
  return out;
}

function fromRow(table, row) {
  const map = camelToSnakeMaps[table] || {};
  const inv = Object.fromEntries(Object.entries(map).map(([c, s]) => [s, c]));
  const out = {};
  for (const [k, v] of Object.entries(row)) out[inv[k] || k] = v;
  return out;
}

export function useTable(table, filter = {}, ready = true) {
  const [rows, setRows] = useState([]);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const load = useCallback(async () => {
    if (!ready) return;
    let q = supabase.from(table).select("*");
    for (const [k, v] of Object.entries(filterRef.current)) q = q.eq(k, v);
    const { data, error } = await q.order("created_at", { ascending: true });
    if (!error && data) setRows(data.map(r => fromRow(table, r)));
  }, [table, ready]);

  useEffect(() => { load(); }, [load]);

  const dispatch = useCallback(async (action) => {
    if (action.type === "ADD") {
      const row = { ...toRow(table, action.item), ...filterRef.current };
      const { data, error } = await supabase.from(table).insert(row).select().single();
      if (!error && data) { const m = fromRow(table, data); setRows(prev => [...prev, m]); return m; }
    } else if (action.type === "UPDATE") {
      const changes = toRow(table, action.changes);
      const { data, error } = await supabase.from(table).update(changes).eq("id", action.id).select().single();
      if (!error && data) { const m = fromRow(table, data); setRows(prev => prev.map(r => r.id === action.id ? m : r)); return m; }
    } else if (action.type === "DELETE") {
      const { error } = await supabase.from(table).delete().eq("id", action.id);
      if (!error) setRows(prev => prev.filter(r => r.id !== action.id));
    }
    return null;
  }, [table]);

  return [rows, dispatch, load];
}
